import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, X, Search } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { ticketsApi } from './ticketsApi';
import { UserAvatar } from '@/features/users/UserAvatar';
import type { TicketWatcher } from './schema';

/**
 * Watchers (CC list) panel on the ticket detail. Visible to the requester + IT.
 * Lists current watchers and offers a Graph-backed people-picker to add more.
 * Mutations invalidate the ticket detail so the list refreshes.
 */
export function WatchersPanel({
  ticketId,
  ticketNumber,
  watchers
}: {
  ticketId: string;
  ticketNumber: number;
  watchers: TicketWatcher[];
}) {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const search = useQuery({
    queryKey: ['directory', debounced],
    queryFn: ({ signal }) => ticketsApi.searchDirectory(debounced, getAccessToken, signal),
    enabled: debounced.length >= 2
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tickets', 'detail', ticketNumber] });

  const add = useMutation({
    mutationFn: (oid: string) => ticketsApi.addWatcher(ticketId, oid, getAccessToken),
    onSuccess: () => { setTerm(''); setDebounced(''); void invalidate(); }
  });
  const remove = useMutation({
    mutationFn: (userId: string) => ticketsApi.removeWatcher(ticketId, userId, getAccessToken),
    onSuccess: () => void invalidate()
  });

  const existingOids = new Set(watchers.map((w) => w.entraObjectId.toLowerCase()));

  return (
    <div className="rounded-xl border bg-card p-4 mb-6">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> Watchers
      </div>

      {watchers.length > 0 ? (
        <ul className="space-y-1.5 mb-3">
          {watchers.map((w) => (
            <li key={w.id} className="flex items-center gap-2 text-sm">
              <UserAvatar oid={w.entraObjectId} name={w.displayName} />
              <span className="font-medium">{w.displayName}</span>
              <span className="text-xs text-muted-foreground truncate">{w.email}</span>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(w.id)}
                className="ml-auto text-muted-foreground hover:text-destructive disabled:opacity-50"
                title={`Remove ${w.displayName}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground mb-3">No watchers yet.</p>
      )}

      <div className="relative">
        <div className="flex items-center gap-2 rounded-md border bg-background px-2.5">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Add a watcher — search by name or email"
            className="flex-1 bg-transparent py-1.5 text-sm outline-none"
          />
        </div>

        {debounced.length >= 2 && (
          <div className="absolute z-10 mt-1 w-full rounded-md border bg-card shadow-lg max-h-64 overflow-auto">
            {search.isPending && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
            {search.data?.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No matches.</div>}
            {search.data?.map((u) => {
              const already = existingOids.has(u.oid.toLowerCase());
              return (
                <button
                  key={u.oid}
                  type="button"
                  disabled={already || add.isPending}
                  onClick={() => add.mutate(u.oid)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                >
                  <UserAvatar oid={u.oid} name={u.displayName} />
                  <span className="min-w-0">
                    <span className="block font-medium truncate">{u.displayName}</span>
                    <span className="block text-xs text-muted-foreground truncate">{u.email ?? u.jobTitle ?? ''}</span>
                  </span>
                  {already && <span className="ml-auto text-xs text-muted-foreground">added</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {(add.isError || remove.isError) && (
        <div className="mt-2 text-xs text-destructive">Couldn't update watchers — try again.</div>
      )}
    </div>
  );
}
